import type {
  ApiReturnAllSensor,
  ApiReturnAutomaticSensor,
  ApiReturnDeviceSite,
  ApiReturnDeviceType,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnMeasurePoint,
  ApiReturnMeasureType,
  ApiReturnProject,
  ApiReturnProjectSite,
  ApiReturnSite,
  ApiReturnThematic,
} from '@highimpact-frontend/api-types';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import CreateAutomaticDevice from '../../../../components/devices/automaticDevice/create/CreateAutomaticDevice';
import UpdateAutomaticDevice from '../../../../components/devices/automaticDevice/update/UpdateAutomaticDevice';
import ListDataGrid from '../../../../components/utils/MUISpecifics/data-grid/ListDataGrig';
import { useCheckPermission } from '../../../../components/utils/permission/helpers/usePermission';
import PermissionDecoratorV2 from '../../../../components/utils/permission/PermissionDecoratorV2';
import { resetExternalState } from '../../../../store/data/slice';
import { useAppDispatch } from '../../../../store/hooks';
import { useSubmit } from '../../../../utils/hooks/useSubmit';
import { useTranslation } from 'react-i18next';
import DeviceService from '../../../../api/services/config/device/DeviceService';
import ConfirmModal from '../../../../components/utils/confirmModal/ConfirmModal';
import {
  getDeviceSiteFromSiteAndDevice,
  getSitesFromDevice,
} from '../../../../store/config';
import { DataState } from '../../../../store/data/state';
import { getEntityById, getOrElse } from '../../../../utils/functions';
import { useModal } from '../../../../utils/hooks/useModal';

type RowMUI = Partial<Omit<ApiReturnAutomaticSensor, 'id' | 'type'>> & {
  id: number;
  automaticSensor: ApiReturnAutomaticSensor;
  type?: string;
  model?: string;
  site?: string;
  serialNumber?: string;
};

interface AutomaticSensorsProps {
  automaticSensors: ApiReturnAutomaticSensor[];
  thematics: ApiReturnThematic[];
  projects: ApiReturnProject[];
  measure_points: ApiReturnMeasurePoint[];
  project_sites: ApiReturnProjectSite[];
  sites: DataState<string, { [KEY in number]: ApiReturnSite }>;
  device_sites: DataState<string, { [KEY in number]: ApiReturnDeviceSite }>;
  measureTypes: DataState<string, { [KEY in number]: ApiReturnMeasureType }>;
  deviceTypes: ApiReturnDeviceType[];
  deviceTypeMeasureTypes: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceTypeMeasureType }
  >;
}

const AutomaticSensors: React.FC<AutomaticSensorsProps> = ({
  automaticSensors,
  sites,
  measureTypes,
  measure_points,
  deviceTypes,
  device_sites,
  deviceTypeMeasureTypes,
}) => {
  const { t } = useTranslation('components.devices');
  //const { t: t2 } = useTranslation('collections');

  const dispatch = useAppDispatch();
  const { submit } = useSubmit();
  const createModal = useModal();
  const editModal = useModal<{
    automaticSensor: ApiReturnAutomaticSensor | null;
  }>();

  const deleteModal = useModal<{
    callback: () => void;
    message: string;
    target: string;
    automaticSensorId: number;
  }>();
  const { shouldHide: hideEditButton, canDisable: disableEditButton } =
    useCheckPermission('configuration_automatic_device_edit_button');
  const { shouldHide: hideDeleteButton, canDisable: disableDeleteButton } =
    useCheckPermission('configuration_automatic_device_delete_button');

  const getActiveSite = (automaticSensor: ApiReturnAutomaticSensor) => {
    return getSitesFromDevice(automaticSensor, device_sites, sites)[0]
      ? getSitesFromDevice(automaticSensor, device_sites, sites)[0]
      : null;
  };

  const getDeviceBrands = () => {
    const s = new Set(
      automaticSensors
        .map((md) => md.brand)
        .filter((b): b is string => b != null)
    );
    return [...s];
  };

  const getDeviceSite = (
    automatic_sensor: ApiReturnAllSensor,
    site: ApiReturnSite
  ) => {
    const device_site = getDeviceSiteFromSiteAndDevice(
      automatic_sensor,
      site,
      device_sites
    );
    return (
      measure_points.find((mp) => mp.device_site?.id === device_site?.id)
        ?.name ?? 'Point de mesure inconue'
    );
  };

  const deleteAutomaticSensor = async (id: number) => {
    submit({
      callbacks: [
        async () => DeviceService.delete(id),
        async () => dispatch(resetExternalState({ external_key: 'devices' })),
        async () =>
          dispatch(resetExternalState({ external_key: 'site_devices' })),
        async () =>
          dispatch(
            resetExternalState({ external_key: 'device_measure_types' })
          ),
        async () =>
          dispatch(
            resetExternalState({ external_key: 'device_type_measure_types' })
          ),
      ],
      errorMessages: {
        anyError: t(
          'Une erreur est survenue lors de la suppression du capteur connecté'
        ),
        unicityError: 'Vous avez dèja supprimé ce capteur connecté',
      },
      successMessage: t('Le capteur connecté a été supprimé avec succès'),
    });
  };

  /**
   * Pretty names for headers,
   * here is the names for heder columns names, that are basic configuration
   * and don't need extra options. The cells in header, that need extra options
   * are defined in the column definition below.
   */
  const headingsPrettyNames: Record<string, string> = {
    type: t('Type'),
    model: t('Modèle'),
  };

  const createRow = (automaticSensor: ApiReturnAutomaticSensor): RowMUI => {
    const deviceType = getEntityById(
      automaticSensor.device_type_id!,
      deviceTypes
    );
    return {
      id: automaticSensor.id,
      serialNumber: getOrElse(automaticSensor.sn),
      type: getOrElse(deviceType?.name),
      model: getOrElse(automaticSensor.brand),
      site: getOrElse(getActiveSite(automaticSensor)?.name),
      automaticSensor,
    };
  };

  const rows: RowMUI[] = automaticSensors.map(createRow);

  const createColumn = (field: keyof RowMUI): GridColDef<RowMUI> => ({
    field,
    headerName: headingsPrettyNames[field] ?? field,
    flex: 1,
    minWidth: 100,
    sortable: true,
    filterable: true,
    headerClassName: 'list-data-grid-style_table-header',
  });

  const columns: GridColDef<RowMUI>[] = [
    ...(Object.keys(headingsPrettyNames) as (keyof RowMUI)[]).map((key) =>
      createColumn(key)
    ),

    {
      field: 'serialNumber',
      headerName: t('Numéro de série'),
      sortable: false,
      disableColumnMenu: true,
      minWidth: 100,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },

    {
      field: 'site',
      headerName: t('Position du site'),
      sortable: false,
      disableColumnMenu: true,
      minWidth: 100,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },

    {
      field: 'actions',
      type: 'actions',
      headerName: t('Actions'),
      minWidth: 100,
      headerClassName: 'list-data-grid-style_table-header',
      getActions: (
        params: import('@mui/x-data-grid-pro').GridRowParams<RowMUI>
      ) => {
        const row = params.row as RowMUI;
        return [
          // <GridActionsCellItem
          //   key={'detail'}
          //   icon={<MoreHorizIcon />}
          //   label="Détails"
          //   onClick={() => {
          //     navigate(`/management/devices/${row.automaticSensor.id}`)
          //   }}
          //   showInMenu
          // />,
          !hideEditButton && (
            <GridActionsCellItem
              key={`update-${row.id}-${row.automaticSensor.brand}`}
              icon={<EditIcon />}
              label={t('Modifier')}
              onClick={() => {
                editModal.open({ automaticSensor: params.row.automaticSensor });
              }}
              showInMenu
              disabled={disableEditButton}
            />
          ),
          !hideDeleteButton && (
            <GridActionsCellItem
              key={`delete-${row.id}-${row.automaticSensor.sn}`}
              icon={<DeleteIcon />}
              label={t('Supprimer')}
              onClick={() => {
                deleteModal.open({
                  callback: () =>
                    deleteAutomaticSensor(params.row.automaticSensor.id),
                  message: getActiveSite(params.row.automaticSensor)?.name
                    ? t(
                        'Attention la suppression de ce capteur connecté va entraîner la suppression de la station sur le site',
                        {
                          deviceName: getDeviceSite(
                            params.row.automaticSensor,
                            getActiveSite(params.row.automaticSensor)!
                          ),
                          siteName: getActiveSite(row.automaticSensor)?.name,
                        }
                      )
                    : t('Voulez vous vraiment supprimer ce capteur connecté'),
                  target: '',
                  automaticSensorId: params.row.automaticSensor.id,
                });
              }}
              showInMenu
              disabled={disableDeleteButton}
            />
          ),
        ].filter(Boolean) as React.ReactElement[];
      },
    },
  ];

  return (
    <div className={`list-container`}>
      <div className="toolbar">
        <PermissionDecoratorV2
          item={'configuration_automatic_device_create_button'}
        >
          <button
            type="submit"
            className="uk-button uk-button-default"
            onClick={() => createModal.open()}
          >
            {t("Ajouter un capteur connecté à l'inventaire")}
          </button>
        </PermissionDecoratorV2>
      </div>

      <ListDataGrid
        rows={rows}
        columns={columns}
        checkboxSelection={false}
        height="calc(100vh - 80px - 200px)"
        /*Props that allows define customizations for header row (all cells in header*/
        slotProps={{
          columnMenu: {
            slots: {
              columnMenuSortItem: null,
              columnMenuColumnsItem: null,
              columnMenuPinningItem: null,
            },
          },
        }}
      />

      {deleteModal.isOpen && (
        <ConfirmModal
          onCancel={deleteModal.close}
          onConfirm={() => deleteModal.params?.callback?.()}
          open={deleteModal.isOpen}
        >
          <p>
            {deleteModal.params?.message!}{' '}
            <span className="uk-text-bold">{deleteModal.params?.target}</span> ?
          </p>
        </ConfirmModal>
      )}

      {editModal.isOpen && (
        <UpdateAutomaticDevice
          onCancel={editModal.close}
          isOpen={editModal.isOpen}
          closeModal={editModal.close}
          deviceTypes={deviceTypes}
          deviceTypeMeasureTypes={deviceTypeMeasureTypes}
          measureTypes={measureTypes}
          models={getDeviceBrands()}
          automaticSensor={editModal.params!['automaticSensor']!}
        />
      )}

      {createModal.isOpen && (
        <CreateAutomaticDevice
          onCancel={createModal.close}
          isOpen={createModal.isOpen}
          closeModal={createModal.close}
          devices={automaticSensors}
          models={getDeviceBrands()}
          deviceTypes={deviceTypes}
          measureTypes={measureTypes}
          deviceTypeMeasureTypes={deviceTypeMeasureTypes}
        />
      )}
    </div>
  );
};

export default AutomaticSensors;
